import { useCallback, useEffect } from 'react'

import { useNavigate, useParams } from 'react-router-dom'

import { ScheduleSummaryProps } from 'src/Components/ScheduleSummary'
import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { useCurrency } from 'src/Hooks/useCurrency'
import { PaymentMethodEnum } from 'src/Constants/enums'
import { getPaymentMethod } from 'src/Redux/payment-process/selectors'
import { setPaymentAlertError } from 'src/Redux/payment-process'
import { PaymentErrors } from 'src/Components/PaymentErrorsAlert'
import { getOrderCheckoutPaymentError } from 'src/Utils/orderCheckoutPaymentError'
import { refetchUsersMe } from 'src/Redux/userInfoApi'
import { ScheduleSteps } from 'src/Types/ui'
import { ArtistRoutesEnum } from 'src/Router/ArtistComponentsNewUI'
import { createPaymentUrls } from 'src/Hooks/useStripePayment'
import { useAdsOrderCheckoutConfirmEvents } from 'src/Hooks/useAdsOrderCheckoutConfirmEvents'
import { useGetBillingAgreementQuery } from 'src/Redux/billingAgreementApi'
import { useGetMetaAdsCampaignByIdQuery } from 'src/Redux/adsCampaignApi'
import { isCountryLoaded } from 'src/Redux/auth-process/countrySlice/selectors'
import { getUserCountry } from 'src/Redux/auth-process/countrySlice/api-actions'
import {
  OrderCheckoutInfoResponse,
  OrderItemBreakdown,
  OrderPaymentMethod,
  useCreateOrderCheckoutMutation,
} from 'src/Redux/artistOrderApi'
import { getIsOrderFullyCovered } from 'src/Utils/displayedPaymentMethods'

import { useOrderCheckoutInfo } from './useOrderCheckoutInfo'

const ORDER_PAYMENT_METHOD_MAP: Partial<
  Record<PaymentMethodEnum, OrderPaymentMethod>
> = {
  [PaymentMethodEnum.STRIPE]: OrderPaymentMethod.StripeSetupIntent,
  [PaymentMethodEnum.PAYPAL]: OrderPaymentMethod.PaypalOneTime,
  [PaymentMethodEnum.PAYPAL_BILLING_AGREEMENT]:
    OrderPaymentMethod.PaypalBillingAgreement,
}

const ORDER_PAYMENT_METHOD_TO_ENUM: Partial<
  Record<OrderPaymentMethod, PaymentMethodEnum>
> = {
  [OrderPaymentMethod.StripeSetupIntent]: PaymentMethodEnum.STRIPE,
  [OrderPaymentMethod.PaypalOneTime]: PaymentMethodEnum.PAYPAL,
  [OrderPaymentMethod.PaypalBillingAgreement]:
    PaymentMethodEnum.PAYPAL_BILLING_AGREEMENT,
}

const resolveOrderPaymentMethod = (
  paymentMethod: PaymentMethodEnum,
): OrderPaymentMethod | undefined => {
  if (paymentMethod === PaymentMethodEnum.PAYPAL) {
    return OrderPaymentMethod.PaypalBillingAgreement
  }
  return ORDER_PAYMENT_METHOD_MAP[paymentMethod]
}

const mapAvailablePaymentMethods = (
  methods?: OrderPaymentMethod[],
  paypalEmail?: string,
): PaymentMethodEnum[] | undefined =>
  methods?.flatMap((method): PaymentMethodEnum[] => {
    const mapped = ORDER_PAYMENT_METHOD_TO_ENUM[method]
    if (!mapped) {
      return []
    }
    if (method === OrderPaymentMethod.PaypalBillingAgreement) {
      return paypalEmail ? [mapped] : [PaymentMethodEnum.PAYPAL]
    }
    return [mapped]
  })

const mapItemBreakdowns = (
  items: OrderCheckoutInfoResponse['items'],
): OrderItemBreakdown[] =>
  items.map((item) => ({
    id: item.id,
    fullPrice: item.fullPrice,
    appliedBalance: item.appliedBalance,
    payable: item.payable,
    itemDiscount: item.itemDiscount,
    vatAmount: item.vatAmount,
  }))

type UseAdsScheduleOrderSummary = ScheduleSummaryProps & {
  availablePaymentMethods?: PaymentMethodEnum[]
  isOrderFullyCovered: boolean
}

export const useAdsScheduleOrderSummary = (): UseAdsScheduleOrderSummary => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { number } = useParams()
  const campaignId = Number(number)

  const { data: metaAdsCampaign, isFetching: isCampaignFetching } =
    useGetMetaAdsCampaignByIdQuery(campaignId)

  const paymentMethod = useAppSelector(getPaymentMethod)
  const countryLoaded = useAppSelector(isCountryLoaded)
  const { data: billingData } = useGetBillingAgreementQuery()

  const budget = metaAdsCampaign?.totalBudget || 0

  const { calculateCurrencyValue } = useCurrency(CampaignPlatformTypeEnum.META)

  const triggerConfirmPaymentEvents = useAdsOrderCheckoutConfirmEvents(
    campaignId,
    budget,
  )

  const [createOrderCheckout, { isLoading }] = useCreateOrderCheckoutMutation()

  const checkoutInfoData = useOrderCheckoutInfo({
    campaignId,
    budget,
    isCampaignFetching,
  })

  useEffect(() => {
    if (!countryLoaded) {
      dispatch(getUserCountry())
    }
  }, [dispatch, countryLoaded])

  const handleClickContinue = useCallback((): void => {
    if (!checkoutInfoData || !metaAdsCampaign) {
      return
    }

    const orderPaymentMethod = resolveOrderPaymentMethod(paymentMethod)

    if (
      !orderPaymentMethod ||
      !checkoutInfoData.paymentMethods.includes(orderPaymentMethod)
    ) {
      dispatch(setPaymentAlertError(PaymentErrors.DEFAULT))
      return
    }

    const { startDate } = metaAdsCampaign
    const { successUrl, cancelUrl } = createPaymentUrls({
      campaignId,
      startDate,
      type: CampaignPlatformTypeEnum.META,
      budget: checkoutInfoData.breakdown.totalFullPrice,
    })

    triggerConfirmPaymentEvents(paymentMethod)

    createOrderCheckout({
      paymentMethod: orderPaymentMethod,
      coupons: [],
      itemBreakdowns: mapItemBreakdowns(checkoutInfoData.items),
      successUrl,
      cancelUrl,
    })
      .unwrap()
      .then((result) => {
        if (result.checkoutSessionUrl) {
          window.location.href = result.checkoutSessionUrl
          return
        }

        navigate(
          `${ArtistRoutesEnum.AdsSchedule}/${campaignId}?step=${ScheduleSteps.CHECKOUT}&paymentStatus=success&startDate=${encodeURIComponent(startDate)}`,
        )
        void dispatch(refetchUsersMe())
      })
      .catch((error: unknown) => {
        dispatch(setPaymentAlertError(getOrderCheckoutPaymentError(error)))
      })
  }, [
    campaignId,
    checkoutInfoData,
    createOrderCheckout,
    dispatch,
    metaAdsCampaign,
    navigate,
    paymentMethod,
    triggerConfirmPaymentEvents,
  ])

  const breakdown = checkoutInfoData?.breakdown

  return {
    availablePaymentMethods: mapAvailablePaymentMethods(
      checkoutInfoData?.paymentMethods,
      billingData?.paypalEmail,
    ),
    isOrderFullyCovered: getIsOrderFullyCovered(breakdown, false),
    onConfirm: handleClickContinue,
    paymentLoading: isLoading,
    price: calculateCurrencyValue(breakdown?.totalFullPrice),
    discount: '',
    vat: breakdown?.totalVatAmount
      ? `+ ${calculateCurrencyValue(breakdown.totalVatAmount)}`
      : '',
    balance: `- ${calculateCurrencyValue(breakdown?.totalAppliedBalance)}`,
    total: calculateCurrencyValue(breakdown?.totalPayable),
    showPromocode: false,
    showTooltip: false,
    isBalanceHidden: true,
    artistName: metaAdsCampaign?.ownerName || '',
    trackName: metaAdsCampaign?.resourceName || '',
    platform: CampaignPlatformTypeEnum.META,
    onApplyPromocode: async () => null as never,
    matchingType: null,
  }
}
